import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoneCall, UserCheck } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useSocket } from '../lib/socket';
import { Avatar } from './Avatar';
import { Modal } from './Modal';
import { atendimentoApi } from '../modules/atendimento/lib/api';

interface PayloadModal {
  id: number;
  nome: string | null;
  telefone: string | null;
  jid: string;
  foto_url: string | null;
  atendente_nome: string | null;
  min_parado: number;
  qtd_agentes: number;
}

// Modal de alerta em tempo real pra admin/gerente quando o rodizio de atendentes
// se esgota e um chamado fica parado (DA-114). Dispensavel — ao fechar cai pro
// banner ambar persistente. Dedup por spell (1 modal por chamado por encaminhamento).
// NAO reabre apos dispensar (Set em memoria guarda os ids ja vistos nesta sessao).
export function ModalChamadoParado() {
  const { usuario } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const [payload, setPayload] = useState<PayloadModal | null>(null);
  // Ids ja vistos nesta sessao (evita reabrir ao receber socket duplicado)
  const [vistos] = useState(() => new Set<number>());

  const podeVer = usuario && ['admin', 'gerente'].includes(usuario.perfil);

  const fechar = useCallback(() => setPayload(null), []);

  useEffect(() => {
    if (!podeVer || !socket) return;
    function onModalParado(data: PayloadModal) {
      if (vistos.has(data.id)) return; // ja mostrado nesta sessao
      vistos.add(data.id);
      setPayload(data);
    }
    socket.on('chamado_parado_modal', onModalParado);
    return () => { socket.off('chamado_parado_modal', onModalParado); };
  }, [podeVer, socket, vistos]);

  async function assumir() {
    if (!payload) return;
    try {
      await atendimentoApi.atualizarConversa(payload.id, { usuario_atribuido_id: usuario!.id });
    } catch {
      // silencia — a navegacao ja abre o chat mesmo se o PUT falhar
    }
    fechar();
    navigate('/atendimento');
  }

  function verChamado() {
    fechar();
    navigate('/atendimento');
  }

  if (!payload) return null;

  const primeiroNome = (nome: string | null) => {
    if (!nome) return '—';
    const fn = nome.replace(/\s+FIODECOBRE$/i, '').trim().split(/\s+/)[0] ?? nome;
    return fn ? fn.charAt(0).toUpperCase() + fn.slice(1).toLowerCase() : nome;
  };

  const contexto = payload.qtd_agentes <= 1
    ? `${primeiroNome(payload.atendente_nome)} (unica em turno) — ninguem respondeu`
    : `rodizio de ${payload.qtd_agentes} atendentes esgotado — nenhuma respondeu`;

  const COR = '#a16207'; // ambar — daltonismo-safe

  return (
    <Modal
      aberto
      onFechar={fechar}
      titulo="Chamado parado"
      largura={440}
      compacto
      rodape={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={verChamado}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'none', border: `1px solid ${COR}`, borderRadius: 6,
              color: COR, fontWeight: 600, fontSize: 13, padding: '6px 14px', cursor: 'pointer',
            }}
          >
            <PhoneCall size={14} />
            Ver chamado
          </button>
          <button
            type="button"
            onClick={() => void assumir()}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: COR, border: 'none', borderRadius: 6,
              color: '#fff', fontWeight: 600, fontSize: 13, padding: '6px 14px', cursor: 'pointer',
            }}
          >
            <UserCheck size={14} />
            Assumir
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '4px 0 8px' }}>
        <Avatar
          nome={payload.nome || payload.telefone || '?'}
          fotoUrl={payload.foto_url}
          tamanho={44}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2, color: 'var(--text-color, #111)' }}>
            {payload.nome || payload.telefone || payload.jid}
          </div>
          {payload.nome && payload.telefone && (
            <div style={{ fontSize: 12, color: 'var(--text-muted, #888)', marginBottom: 4 }}>
              {payload.telefone}
            </div>
          )}
          <div
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: `rgba(161,98,7,0.12)`, border: `1px solid rgba(161,98,7,0.30)`,
              borderRadius: 6, padding: '4px 10px', color: COR, fontSize: 13, fontWeight: 600,
            }}
          >
            ⏱ sem resposta ha {payload.min_parado} min
          </div>
          <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-color, #333)' }}>
            {contexto}
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted, #888)' }}>
            Fechar descarta este aviso. O banner ambar continua ate o chamado ser resolvido.
          </div>
        </div>
      </div>
    </Modal>
  );
}
